import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, History, PlusCircle, RefreshCcw } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { db, isFirebaseConfigured } from '../config/firebase';
import {
  collection,
  doc,
  getDoc,
  limit,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  getDocs,
} from 'firebase/firestore';

const normalizeToken = (value) => String(value || '').trim().toUpperCase();

const ensureVariantStock = ({ colors, sizes, rawVariantStock }) => {
  const safeColors = Array.isArray(colors) ? colors.map(normalizeToken).filter(Boolean) : [];
  const safeSizes = Array.isArray(sizes) ? sizes.map(normalizeToken).filter(Boolean) : [];
  const raw = rawVariantStock && typeof rawVariantStock === 'object' ? rawVariantStock : {};
  const out = {};
  for (const c of safeColors) {
    for (const s of safeSizes) {
      const key = `${c}|${s}`;
      const n = Number(raw[key]);
      out[key] = Number.isFinite(n) && n >= 0 ? n : 0;
    }
  }
  return out;
};

const sumStock = (variantStock) => {
  const raw = variantStock && typeof variantStock === 'object' ? variantStock : {};
  return Object.values(raw).reduce((sum, v) => sum + (Number(v) || 0), 0);
};

const getDraftKey = (productId) => `ditris:stockDraft:${String(productId || '')}`;

const AdminProductStocks = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('add');
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [product, setProduct] = useState(null);

  const [deltaByKey, setDeltaByKey] = useState({});
  const [note, setNote] = useState('');

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const colors = useMemo(() => {
    const list = Array.isArray(product?.colors) ? product.colors : [];
    return list.map(normalizeToken).filter(Boolean);
  }, [product?.colors]);

  const sizes = useMemo(() => {
    const list = Array.isArray(product?.sizes) ? product.sizes : [];
    return list.map(normalizeToken).filter(Boolean);
  }, [product?.sizes]);

  const currentVariantStock = useMemo(() => {
    return ensureVariantStock({
      colors,
      sizes,
      rawVariantStock: product?.variantStock,
    });
  }, [colors, sizes, product?.variantStock]);

  const refresh = async () => {
    if (!id) return;
    if (!isFirebaseConfigured || !db) {
      toast({
        title: 'Firebase not configured',
        description: 'Stock management requires Firebase configuration.',
      });
      return;
    }

    setIsLoading(true);
    try {
      const snap = await getDoc(doc(db, 'products', String(id)));
      if (!snap.exists()) {
        setProduct(null);
        return;
      }
      const data = { id: snap.id, ...snap.data() };
      setProduct(data);
    } catch (e) {
      toast({ title: 'Failed to load product', description: String(e?.message || 'Please try again.') });
    } finally {
      setIsLoading(false);
    }
  };

  const refreshHistory = async () => {
    if (!id) return;
    if (!isFirebaseConfigured || !db) return;
    setHistoryLoading(true);
    try {
      const ref = collection(db, 'products', String(id), 'stockHistory');
      const q = query(ref, orderBy('createdAt', 'desc'), limit(50));
      const snap = await getDocs(q);
      setHistory(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, [id]);

  useEffect(() => {
    if (activeTab !== 'history') return;
    refreshHistory();
  }, [activeTab, id]);

  const mergeInputsFromProduct = () => {
    const base = currentVariantStock;
    const keys = Object.keys(base);
    setDeltaByKey((prev) => {
      const raw = prev && typeof prev === 'object' ? prev : {};
      const next = {};
      keys.forEach((k) => {
        next[k] = Object.prototype.hasOwnProperty.call(raw, k) ? raw[k] : '';
      });
      return next;
    });
  };

  useEffect(() => {
    if (!product?.id) return;
    mergeInputsFromProduct();
  }, [product?.id, colors.join('|'), sizes.join('|')]);

  useEffect(() => {
    if (!id) return;
    try {
      const raw = window.localStorage.getItem(getDraftKey(id));
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        if (parsed.deltaByKey && typeof parsed.deltaByKey === 'object') setDeltaByKey(parsed.deltaByKey);
        if (typeof parsed.note === 'string') setNote(parsed.note);
      }
    } catch {
      // ignore
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;
    try {
      window.localStorage.setItem(getDraftKey(id), JSON.stringify({ deltaByKey, note }));
    } catch {
      // ignore
    }
  }, [id, deltaByKey, note]);

  const validateDeltaMap = (map) => {
    const out = {};
    for (const [k, raw] of Object.entries(map || {})) {
      if (raw === '' || raw === null || raw === undefined) continue;
      const n = Number(raw);
      if (!Number.isFinite(n)) return { ok: false, error: `Invalid number for ${k}` };
      if (n === 0) continue;
      out[k] = n;
    }
    if (Object.keys(out).length === 0) return { ok: false, error: 'Enter at least one non-zero quantity.' };
    return { ok: true, values: out };
  };

  const commitAddStocks = async () => {
    if (!id) return;
    const parsed = validateDeltaMap(deltaByKey);
    if (!parsed.ok) {
      toast({ title: 'Invalid inputs', description: parsed.error });
      return;
    }

    setSaving(true);
    try {
      await runTransaction(db, async (tx) => {
        const productRef = doc(db, 'products', String(id));
        const snap = await tx.get(productRef);
        if (!snap.exists()) throw new Error('Product not found');

        const base = snap.data() || {};
        const baseColors = Array.isArray(base.colors) ? base.colors : [];
        const baseSizes = Array.isArray(base.sizes) ? base.sizes : [];
        const current = ensureVariantStock({
          colors: baseColors,
          sizes: baseSizes,
          rawVariantStock: base.variantStock,
        });

        const changes = parsed.values;
        const nextVariantStock = { ...current };
        for (const [k, delta] of Object.entries(changes)) {
          const prev = Number(nextVariantStock[k] || 0);
          const next = prev + Number(delta);
          nextVariantStock[k] = next < 0 ? 0 : next;
        }

        const beforeTotal = sumStock(current);
        const afterTotal = sumStock(nextVariantStock);

        tx.update(productRef, {
          variantStock: nextVariantStock,
          stockQuantity: afterTotal,
          inStock: afterTotal > 0,
          updatedAt: serverTimestamp(),
        });

        const historyRef = collection(db, 'products', String(id), 'stockHistory');
        const historyDocRef = doc(historyRef);
        tx.set(historyDocRef, {
          type: 'add',
          changes,
          note: String(note || '').trim() || null,
          beforeTotal,
          afterTotal,
          createdAt: serverTimestamp(),
        });
      });

      toast({ title: 'Stocks added', description: 'Inventory updated successfully.' });
      setNote('');
      try {
        window.localStorage.removeItem(getDraftKey(id));
      } catch {
        // ignore
      }
      await refresh();
      if (activeTab === 'history') await refreshHistory();
    } catch (e) {
      toast({ title: 'Update failed', description: String(e?.message || 'Please try again.') });
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'add', label: 'Add Stocks', icon: PlusCircle },
    { id: 'history', label: 'Stock Update History', icon: History },
  ];

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="text-sm text-gray-600">Loading stock manager...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="text-sm text-gray-600">Product not found.</div>
        <button
          type="button"
          onClick={() => navigate('/admin/products')}
          className="mt-4 inline-flex items-center gap-2 text-sm underline hover:opacity-60"
        >
          <ArrowLeft size={16} />
          Back to products
        </button>
      </div>
    );
  }

  const total = sumStock(currentVariantStock);

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/admin/products')}
              className="inline-flex items-center justify-center w-10 h-10 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 transition-colors"
              aria-label="Back"
            >
              <ArrowLeft size={16} />
            </button>
            <div>
              <h1 className="text-2xl font-medium tracking-wide">STOCK MANAGER</h1>
              <p className="mt-1 text-sm text-gray-600">
                {product?.name || 'Untitled product'}
                <span className="mx-2 text-gray-300">•</span>
                Total available: <span className="font-medium text-black">{total}</span>
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={refresh}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            <RefreshCcw size={16} />
            Refresh
          </button>
          <Link
            to={`/admin/products/${product.id}/edit`}
            className="inline-flex items-center justify-center rounded-lg border border-black px-5 py-3 text-sm font-medium tracking-wide hover:bg-black hover:text-white transition-colors"
          >
            View product
          </Link>
        </div>
      </div>

      <div className="mt-8 bg-white border border-gray-200 rounded-2xl shadow-[0_10px_30px_rgba(0,0,0,0.06)] overflow-hidden">
        <div className="border-b border-gray-200 bg-gray-50">
          <div className="flex flex-col sm:flex-row">
            {tabs.map((t) => {
              const Icon = t.icon;
              const active = activeTab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setActiveTab(t.id)}
                  className={`flex-1 px-4 py-4 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                    active ? 'bg-white text-black' : 'text-gray-600 hover:text-black hover:bg-white/60'
                  }`}
                >
                  <Icon size={16} />
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-8">
              <div className="text-sm font-medium tracking-wide">
                {activeTab === 'add' ? 'ADD STOCKS' : 'HISTORY'}
              </div>
              <div className="mt-2 text-sm text-gray-600">
                {activeTab === 'add'
                  ? 'Enter quantities to add (use negatives to subtract). Only filled cells are applied.'
                  : 'Latest stock updates for this product.'}
              </div>

              {activeTab === 'history' ? (
                <div className="mt-6 border border-gray-200 rounded-xl overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                    <div className="text-xs tracking-widest text-gray-600">EVENTS</div>
                    <button
                      type="button"
                      onClick={refreshHistory}
                      className="text-xs text-gray-600 hover:text-black transition-colors"
                    >
                      Refresh
                    </button>
                  </div>
                  {historyLoading ? (
                    <div className="px-4 py-6 text-sm text-gray-600">Loading history...</div>
                  ) : history.length === 0 ? (
                    <div className="px-4 py-6 text-sm text-gray-600">No stock updates yet.</div>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      {history.map((h) => {
                        const type = String(h?.type || '').toUpperCase();
                        const beforeTotal = Number(h?.beforeTotal ?? 0);
                        const afterTotal = Number(h?.afterTotal ?? 0);
                        const n = String(h?.note || '').trim();
                        return (
                          <div key={h.id} className="px-4 py-4">
                            <div className="flex items-center justify-between gap-4">
                              <div className="text-sm font-medium text-black">{type || 'UPDATE'}</div>
                              <div className="text-xs text-gray-600">
                                Total: <span className="font-medium text-black">{beforeTotal}</span> →{' '}
                                <span className="font-medium text-black">{afterTotal}</span>
                              </div>
                            </div>
                            {n ? <div className="mt-1 text-sm text-gray-600">{n}</div> : null}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-6 overflow-x-auto border border-gray-200 rounded-xl">
                  <table className="min-w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr className="text-left text-xs tracking-widest text-gray-600">
                        <th className="px-4 py-3">COLOR</th>
                        {sizes.map((s) => (
                          <th key={s} className="px-4 py-3">
                            {s}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {colors.map((c) => (
                        <tr key={c} className="border-b border-gray-200 last:border-b-0">
                          <td className="px-4 py-3 text-sm font-medium text-black whitespace-nowrap">{c}</td>
                          {sizes.map((s) => {
                            const key = `${c}|${s}`;
                            const current = Number(currentVariantStock?.[key] ?? 0);
                            const value = deltaByKey?.[key] ?? '';
                            return (
                              <td key={key} className="px-4 py-3 align-top">
                                <div className="text-xs text-gray-500">Current: {current}</div>
                                <input
                                  type="number"
                                  step="1"
                                  value={value}
                                  onChange={(e) => {
                                    const next = e.target.value;
                                    setDeltaByKey((prev) => ({ ...(prev || {}), [key]: next }));
                                  }}
                                  className="mt-2 w-28 rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-black transition-colors"
                                />
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {activeTab !== 'history' ? (
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-800">Note (optional)</label>
                  <input
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="E.g. New shipment arrived / Damaged items removed"
                    className="mt-2 w-full rounded-lg border border-gray-300 px-4 py-3 text-sm outline-none focus:border-black transition-colors"
                  />

                  <div className="mt-4 flex flex-col sm:flex-row gap-3">
                    <button
                      type="button"
                      disabled={saving}
                      onClick={commitAddStocks}
                      className={`inline-flex items-center justify-center gap-2 rounded-lg px-5 py-3 text-sm font-medium transition-colors ${
                        saving ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-black text-white hover:bg-gray-900'
                      }`}
                    >
                      <PlusCircle size={16} />
                      {saving ? 'Saving...' : 'Apply Add Stocks'}
                    </button>
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => {
                        mergeInputsFromProduct();
                        setNote('');
                        try {
                          if (id) window.localStorage.removeItem(getDraftKey(id));
                        } catch {
                          // ignore
                        }
                      }}
                      className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-5 py-3 text-sm font-medium hover:bg-gray-50 transition-colors"
                    >
                      Reset
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminProductStocks;
